import type {AppState} from "../../../../redux/rootReducer";
import {toLowercaseTrim} from "../../../../util/string-util";
import {
  IMySongsPageDifficultyFilter,
  IMySongsPageSelectedSong,
  IMySongsPageStore,
  MySongsPageFilterLessonFeatureType,
} from "./mySongsPage.types";
import {
  ISongDetailsResponseSong,
  ISongDetailsResponseSongLesson,
  ISongDetailsResponseSongLessonPreviews,
  ISongDetailsResponseSongLessonUserDetails
} from "../../../../client/models/songs/ISongDetailsResponse";
import {isUndefined} from "../../../../util/is-type";

const getMySongsPageStore = (store: AppState): IMySongsPageStore =>
  store.mySongsPage;

const getMySongsFilters = (store: AppState) => store.mySongsFilters;

/***** Genres *****/
// user selected genre ids
export const getMySongsPageFilterSelectedGenres = (store: AppState): number[] =>
  getMySongsPageStore(store).genres ?? [];

// user selected genre ids available to filter results
export const getMySongsPageAvailableFilterSelectedGenres = (store: AppState): number[] => {
  const availableIds = getMySongsFilters(store)?.genres?.ids ?? [];
  return getMySongsPageFilterSelectedGenres(store)
    .filter((selectedId) => availableIds.includes(selectedId));
}

export const getIsMySongsPageFilterGenreIdSelected = (store: AppState, genreId: number): boolean =>
  getMySongsPageFilterSelectedGenres(store).includes(genreId);

const getMySongsPageAvailableFilterSelectedGenresTotal = (store: AppState): number =>
  getMySongsPageAvailableFilterSelectedGenres(store).length;

/***** difficulty *****/
const getMySongsPageFilterDifficultyStore = (store: AppState): IMySongsPageDifficultyFilter =>
  getMySongsPageStore(store).difficulty ?? {};

// user selected Difficulty Categories ids
export const getMySongsPageFilterDifficultyCategoriesSelected = (store: AppState): number[] =>
  getMySongsPageFilterDifficultyStore(store).difficultyCategories ?? [];

// user selected Difficulty Categories ids available to filter by
export const getMySongsPageAvailableFilterDifficultyCategoriesSelected = (store: AppState): number[] => {
  const availableDifficultyCategories = getMySongsFilters(store)?.difficultyCategories?.ids ?? [];
  return getMySongsPageFilterDifficultyCategoriesSelected(store)
    .filter((selectedCategoryId) => availableDifficultyCategories.includes(selectedCategoryId))
}

// user selected Difficulty ids
export const getMySongsPageFilterDifficultiesSelected = (store: AppState): number[] =>
  getMySongsPageFilterDifficultyStore(store).difficulties ?? [];

// user selected Difficulty ids available to filter results
export const getMySongsPageAvailableFilterDifficultiesSelected = (store: AppState): number[] => {
  const availableDifficultyIds = (getMySongsFilters(store)?.difficulties ?? []).map(({id}) => id);
  return getMySongsPageFilterDifficultiesSelected(store)
    .filter((selectedId) => availableDifficultyIds.includes(selectedId));
}

export const getMySongsPageFilterResultsToDifficultyIds  = (store: AppState): number[] => {
  return getMySongsPageAvailableFilterDifficultiesSelected(store);
}


export const getIsMySongsPageFilterDifficultyCategorySelected = (store: AppState, difficultyCategoryId: number): boolean =>
  getMySongsPageFilterDifficultyCategoriesSelected(store).includes(difficultyCategoryId);

export const getIsMySongsPageFilterDifficultyCategoryPartiallySelected = (store: AppState, difficultyCategoryId: number): boolean => {

  const isDifficultyCategorySelected = getIsMySongsPageFilterDifficultyCategorySelected(store, difficultyCategoryId);

  if (isDifficultyCategorySelected) {
    // is selected, can't be partial selected
    return false;
  }

  const allSelectedDifficulties = getMySongsPageFilterDifficultiesSelected(store);

  // is not selected, check if some child difficulties are selected
  const selectedDifficultyIdsForDifficultyCategory = (getMySongsFilters(store)?.difficulties ?? [])
    .filter((d) => d.categoryId === difficultyCategoryId)
    .map(({id}) => id)
    .filter((difficultyId) => allSelectedDifficulties.includes(difficultyId));

  return selectedDifficultyIdsForDifficultyCategory.length > 0;

}


export const getIsMySongsPageFilterDifficultyIdSelected = (store: AppState, difficultyId: number): boolean =>
  getMySongsPageFilterDifficultiesSelected(store).includes(difficultyId);

/***** lesson feature type *****/

const getMySongsPageFilterFeatureType = (store: AppState): MySongsPageFilterLessonFeatureType | undefined =>
  getMySongsPageStore(store).features;

export const getMySongsPageFilterFeatureValue = (store: AppState): MySongsPageFilterLessonFeatureType =>
  getMySongsPageFilterFeatureType(store) ?? "all";

export const getMySongsPageFilterLessonVideoOnly = (store: AppState): boolean =>
  getMySongsPageFilterFeatureValue(store) === "lesson.hasVideo";


/***** arrangement type *****/

export const getMySongsPageFilterSelectedArrangementTypes = (store: AppState): number[] =>
  getMySongsPageStore(store).arrangementTypes ?? [];

// user selected arrangement type ids available to filter results
export const getMySongsPageAvailableFilterSelectedArrangementTypes = (store: AppState): number[] => {
  const availableIds = getMySongsFilters(store)?.arrangementTypes?.ids ?? [];
  return getMySongsPageFilterSelectedArrangementTypes(store)
    .filter((selectedId) => availableIds.includes(selectedId));
}

const getMySongsPageFilterResultsToArrangementIds = (store: AppState): number[] => {
  return getMySongsPageAvailableFilterSelectedArrangementTypes(store);
}

export const getIsMySongsPageFilterArrangementTypeIdSelected = (store: AppState, arrangementTypeId: number): boolean =>
  getMySongsPageFilterSelectedArrangementTypes(store).includes(arrangementTypeId);

/***** search input *****/
const getMySongsPageSearchStore = (store: AppState) =>
  getMySongsPageStore(store).search ?? {};

export const getIsMySongsPageSearchFocused = (store: AppState): boolean =>
  !!getMySongsPageSearchStore(store).isFocused;

export const getMySongsPageSearchValue = (store: AppState): string =>
  getMySongsPageSearchStore(store).value ?? ``;

export const getDoesMySongsPageHaveSearchValue = (store: AppState): boolean =>
  getMySongsPageSearchValue(store).trim().length > 0;

/***** all filters *****/
export const getIsMySongsResultsSearchTerm = (store: AppState): string =>
  toLowercaseTrim(getMySongsPageSearchValue(store));

export const getIsMySongsResultsSearched = (store: AppState): boolean =>
  getIsMySongsResultsSearchTerm(store).length > 0;

/***** Dropdown Titles *****/

export const getMySongsPageFilterDifficultyMenuTitle =  (store: AppState): string => {
  const selectedDifficultyCategoryIds: number[] = getMySongsPageAvailableFilterDifficultyCategoriesSelected(store);
  const selectedDifficultiesCount: number = getMySongsPageFilterResultsToDifficultyIds(store).length;
  const totalDifficulties: number = (getMySongsFilters(store)?.difficulties ?? []).length;

  // if none/all "all levels"
  if (!selectedDifficultiesCount || selectedDifficultiesCount === totalDifficulties) {
    // no filter for difficulties
    return "All Levels";
  }

  // if only one category is fully selected, category name
  if (selectedDifficultyCategoryIds.length === 1 && selectedDifficultiesCount === 3) {
    const selectedCategoryId = Number(selectedDifficultyCategoryIds[0]);

    return getMySongsFilters(store)?.difficultyCategories?.byId[selectedCategoryId]?.name || '';
  }

  // if some then count of sub category, i.e. `4/9`
  return `${selectedDifficultiesCount}/${totalDifficulties} Levels`;
}

export const getMySongsPageTypeFilterMenuTitle = (store: AppState): string => {
  const selectedTypes: number = getMySongsPageFilterResultsToArrangementIds(store).length;
  const totalTypes: number = (getMySongsFilters(store)?.arrangementTypes?.ids ?? []).length;
  const isVideoOnly = getMySongsPageFilterFeatureValue(store) === "lesson.hasVideo";
  const videoLabel = isVideoOnly ? `+ Video` : ``;

  if (!selectedTypes || selectedTypes === totalTypes) {
    return `All Types ${videoLabel}`;
  }

  if (selectedTypes === 1) {
    const arrangementTypes = getMySongsFilters(store)?.arrangementTypes;
    const typesLabel: string = getMySongsPageFilterSelectedArrangementTypes(store)
      .map((selectedId) => arrangementTypes?.byId[selectedId]?.name)
      .join(``);
    return `${typesLabel} ${videoLabel}`;
  }

  return `${selectedTypes}/${totalTypes} Types ${videoLabel}`;
}

export const getMySongsPageFilterGenresMenuTitle =  (store: AppState): string => {
  const selectedGenreCount = getMySongsPageAvailableFilterSelectedGenresTotal(store);
  // if no filter, then show as all
  if (!selectedGenreCount) {
    return "All Genres";
  }
  if (selectedGenreCount === 1) {
    const genres = getMySongsFilters(store)?.genres;
    return getMySongsPageAvailableFilterSelectedGenres(store)
      .map((genreId) => genres?.byId[genreId]?.name)
      .join(``);
  }
  return `${selectedGenreCount} Genres`;
};

/***** selected song details *****/
const getMySongsPageSelectedSongStore = (store: AppState): StoreWithRequestStatus<IMySongsPageSelectedSong> =>
  getMySongsPageStore(store).selectedSong ?? {};

const getMySongsPageSelectedSongId = (store: AppState): number | undefined =>
  getMySongsPageSelectedSongStore(store).id;

const getMySongsPageSelectedSongDetailsRequestStatus = (store: AppState): ReduxStoreRequestStatusValueType | undefined =>
  getMySongsPageSelectedSongStore(store).requestStatus;

export const isMySongsPageSelectedSongDetailsRequestReadyToSend = (store: AppState): boolean =>
  isUndefined<ReduxStoreRequestStatusValueType>(getMySongsPageSelectedSongDetailsRequestStatus(store));

export const isMySongsPageSelectedSongDetailsRequestComplete = (store: AppState): boolean =>
  (getMySongsPageSelectedSongDetailsRequestStatus(store) ?? "no-status") === "completed";


export const getMySongsPageSelectedSongPreviewModalContent = (store: AppState) =>
  getMySongsPageSelectedSongStore(store).previewModalContent;

const getMySongsPageSelectedSongDetails = (store: AppState): ISongDetailsResponseSong | undefined => {
  const selectedId = getMySongsPageSelectedSongId(store);
  const songDetails = getMySongsPageSelectedSongStore(store).details;
  if (selectedId !== songDetails?.id) {
    // not details for selected song
    return undefined;
  }
  return songDetails;
}


const getMySongsPageSelectedSongDetailsId = (store: AppState): number | undefined =>
  getMySongsPageSelectedSongDetails(store)?.id;

export const getMySongsPageSongSelectedSongDetailsName = (store: AppState): string | undefined =>
  getMySongsPageSelectedSongDetails(store)?.name;

export const getMySongsPageSongSelectedSongDetailsImageUrl = (store: AppState): string | undefined =>
  getMySongsPageSelectedSongDetails(store)?.image;

export const getMySongsPageSongSelectedSongDetailsArtistName = (store: AppState): string | undefined =>
  getMySongsPageSelectedSongDetails(store)?.artistName;

export const getMySongsPageSongSelectedSongDetailsGenre = (store: AppState): string | undefined =>
  getMySongsPageSelectedSongDetails(store)?.genre;

const getMySongsPageSongSelectedSongDetailsLessons = (store: AppState): ISongDetailsResponseSongLesson[] =>
  getMySongsPageSelectedSongDetails(store)?.lessons ?? [];

export const  isMySongsPageSelectedSongDetailsLoadedWithLessons = (store: AppState): boolean => {
  return (
    isMySongsPageSelectedSongDetailsRequestComplete(store)
    && getMySongsPageSongSelectedSongDetailsLessons(store).length > 0
  );
}

export const  isMySongsPageSelectedSongDetailsLoadedWithoutLessons = (store: AppState): boolean => {
  return (
    isMySongsPageSelectedSongDetailsRequestComplete(store)
    && getMySongsPageSongSelectedSongDetailsLessons(store).length == 0
  );
}

export const getMySongsPageSongSelectedSongDetailsLessonsByDifficultyCategory = (store: AppState, filterDifficultyCategory: string): ISongDetailsResponseSongLesson[] =>
  getMySongsPageSongSelectedSongDetailsLessons(store)
    .filter(({difficultyCategory}) => difficultyCategory === filterDifficultyCategory);


export interface ISongDetailsLessonForListDisplay {
  id: number;
  title: string;
  difficulty: string;
  hasVideo: boolean;
  isInUserLibrary: boolean;
  isFavorited: boolean;
  detailsUrl: string;
}
const selectedSongDetailsLessonsToListDisplay = ({id, arrangementType, difficultySubcategory, hasVideo, userDetails: {isInLibrary, isFavorited}}: ISongDetailsResponseSongLesson, songId: number): ISongDetailsLessonForListDisplay => {

  return {
    id,
    title: arrangementType,
    difficulty: difficultySubcategory,
    hasVideo,
    isInUserLibrary: isInLibrary,
    isFavorited: isFavorited,
    detailsUrl: `/songs/${songId}/${id}`,
  };
}

export const getMySongsPageSongSelectedSongDetailsLessonsForDisplayByDifficultyCategory = (store: AppState, filterDifficultyCategory: string): ISongDetailsLessonForListDisplay[] =>
  getMySongsPageSongSelectedSongDetailsLessonsByDifficultyCategory(store, filterDifficultyCategory)
    .map((lesson) => selectedSongDetailsLessonsToListDisplay(lesson, getMySongsPageSelectedSongDetailsId(store) ?? 0));


export const getMySongsPageSongSelectedSongDetailsFirstLessonId = (store: AppState): number | undefined =>
  getMySongsPageSongSelectedSongDetailsLessons(store)[0]?.id;

const getMySongsPageSongSelectedSongDetailsLessonById = (store: AppState, lessonId: number): ISongDetailsResponseSongLesson | undefined =>
  getMySongsPageSongSelectedSongDetailsLessons(store).find(({id}) => id === lessonId);

export const getMySongsPageSongSelectedSongDetailsLessonDifficultyCategoryById = (store: AppState, lessonId: number) =>
  getMySongsPageSongSelectedSongDetailsLessonById(store, lessonId)?.difficultyCategory;

export const getMySongsPageSongSelectedSongDetailsLessonDifficultySubcategoryById = (store: AppState, lessonId: number) =>
  getMySongsPageSongSelectedSongDetailsLessonById(store, lessonId)?.difficultySubcategory;

export const getMySongsPageSongSelectedSongDetailsLessonArrangementTypeById = (store: AppState, lessonId: number) =>
  getMySongsPageSongSelectedSongDetailsLessonById(store, lessonId)?.arrangementType;

export const getMySongsPageSongSelectedSongDetailsLessonPreviewsById = (store: AppState, lessonId: number): Partial<ISongDetailsResponseSongLessonPreviews> =>
  getMySongsPageSongSelectedSongDetailsLessonById(store, lessonId)?.previews ?? {};

export const getMySongsPageSongSelectedSongDetailsLessonPreviewSheetMusicUrlByLessonId = (store: AppState, lessonId: number): Optional<string> =>
  getMySongsPageSongSelectedSongDetailsLessonPreviewsById(store, lessonId).sheetMusicUrl || undefined;

export const getMySongsPageSongSelectedSongDetailsLessonPreviewWistiaVideoIdByLessonId = (store: AppState, lessonId: number): Optional<string> =>
  getMySongsPageSongSelectedSongDetailsLessonPreviewsById(store, lessonId).wistiaVideoId || undefined;

// Library and favorite states
const getMySongsPageSongSelectedSongDetailsLessonUserDetailsById = (store: AppState, lessonId: number): ISongDetailsResponseSongLessonUserDetails | undefined =>
  getMySongsPageSongSelectedSongDetailsLessonById(store, lessonId)?.userDetails;

export const getMySongsPageSelectedSongDetailsLessonLibraryStateByLessonId = (store: AppState, lessonId: number): boolean =>
  !!getMySongsPageSongSelectedSongDetailsLessonUserDetailsById(store, lessonId)?.isInLibrary;

export const getMySongsPageSelectedSongDetailsLessonFavoriteStateByLessonId = (store: AppState, lessonId: number): boolean =>
  !!getMySongsPageSongSelectedSongDetailsLessonUserDetailsById(store, lessonId)?.isFavorited;

export const getMySongsPageSelectedSongDetailsLessonPurchasedStateByLessonId = (store: AppState, lessonId: number): boolean =>
  !!getMySongsPageSongSelectedSongDetailsLessonUserDetailsById(store, lessonId)?.isPurchased;
